import { call } from 'redux-saga/effects';
import galleryTemplate from 'contentful/ugc-module-content.j2';
import rowTemplate from 'contentful/ugc-pdp-row-module-content.j2';
import carouselTemplate from 'contentful/ugc-pdp-module-content.j2';

import galleryTrayTemplate from 'contentful/ugc-tray.j2';
import carouselTrayTemplate from 'contentful/ugc-pdp-carousel.j2';

import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';
import { fireGATrackingEvent } from '@core/js/utils/analyticsUtils';
import { generate } from '../utils/componentUtils';
import { scrollToElement } from '../../../core/js/utils/scrollUtils';
import { heartClicked } from '../utils/heartsUtils';
import {
    getModules,
    getComponents
} from '../utils/landingPageUtils';
import { mDom } from '../utils/renderUtils';
import {
    isEngage,
    determineNumTilesInRow,
    getNextEligibleTileForTray
} from '../utils/ugcUtils';

import {
    updateButtonTextSaga,
    ANIMATED_DOTS,
    ANIMATED_DOTS_SMALL_DARK
} from '../../../core/js/sagas/animateButtonSagas';
import {
    getUGCPostsSaga,
    getAndSaveInitialPostsSaga,
    getMorePosts,
    getTrayProductsSaga
} from '../sagas/ugcSagas';

import { init as ugcCarouselComponentInit } from './ugcTrayCarouselComponent';

import {
    saveUgcs,
    saveUgcSelectedIndex,
    clearUgcSelectedIndex,
    ugcRendered,
    saveUgcSelectedFilter
} from '../../../core/js/actions/ugcActions';

function onStateChange(store) {
    this.render(store, this, {});
}

function afterRender(store) {
    // Needs to be in a setTimeout for some bug in componentUtils.onStateChange
    window.setTimeout(() => {
        store.dispatch(ugcRendered());
    }, 0);
}

function generateDictionary(state, dictionary, config) {
    const componentIndex = _.get(config, 'multipleIndex', 0);

    dictionary.multipleIndex = componentIndex;

    const ugc = {
        ugc: _.get(state, `core.ugc.${componentIndex}`, {})
    };

    if (state.core.deviceCharacteristics.is_smallish && config.shareYourPicsLink) {
        ugc.ugc.shareYourPicsLink = config.shareYourPicsLink;
    }

    ugc.ugc.isEngage = isEngage(ugc.ugc.config);
    ugc.ugc.displayHearts = isEngage(ugc.ugc.config);
    return Object.assign({}, state, ugc, dictionary);
}

/**
 * UGC data:  grab all product ids that are linked to the tile that was clicked.
 * @param  {object} store The redux store
 * @param {integer} componentIndex ugc component index on the page
 * @param  {integer} clickedTileIndex 1-based array index of clicked tile
 * @returns {array} returns array of numbers that are all linked to the clicked product
 */
export function buildProductIdArray(store, componentIndex, clickedTileIndex) {
    // The index passed in is based off an array that begins with 1.
    // The data in core is indexed using a 0-based array, so we must adjust the number.
    const zeroBasedArrayIndex = clickedTileIndex - 1;

    return _.get(store.getState(), `core.ugc.${componentIndex}.items.${zeroBasedArrayIndex}.product_ids`, []);
}

/**
 * Gets the UGC config data from Contentful
 * @param  {object} store   The redux store
 * @param  {String} feature The parent feature that initialized the UGC component
 * @returns {undefined}      Returns nothing
 */
export function getUgcConfigsFromContentful(store, feature) {
    const contentfulModules = getModules(store, feature);
    const ugcComponents = getComponents(contentfulModules, 'ugcModule');

    return _.map(ugcComponents, (c) => ({
        reelId: _.get(c, 'fields.curalateReelId'),
        pageSize: _.get(c, 'fields.pageSize'),
        filters: _.map(_.get(c, 'fields.filters', []), (f) => f.fields),
        featuredFilter: _.get(c, 'fields.featuredFilter.fields.filterValue'),
        featuredPageSize: _.get(c, 'fields.featuredFilterPageSize'),
        seeMore: true, // This should be improved to not be hard coded in a follow up ticket
        ugcSource: _.get(c, 'fields.ugcSource', 'DASH'), // Type of source.  Curalate, Engage, Dash
        isHearts: _.get(c, 'fields.isHearts'),
        isUploads: _.get(c, 'fields.isUploads'),
        galleryId: _.get(c, 'fields.galleryId')
    }));
}

export function getUgcConfigsForPdp(store) {
    const state = store.getState();
    const ugcConfig = _.get(state, 'core.featureConfigs.browseDisplayOptions.ugcConfig.fields');
    const productStyleNumber = _.get(state, 'product.styleNumber', '');
    const productId = _.get(state, 'product.productId', '');

    if (!ugcConfig) {
        return [];
    }

    return [{
        reelId: ugcConfig.curalateReelId,
        isActive: ugcConfig.isActive,
        pageSize: ugcConfig.pageSize,
        productStyleNumber: productStyleNumber,
        productId: productId,
        filters: ugcConfig.filters,
        displayType: ugcConfig.displayType,
        captionMaxLength: ugcConfig.captionMaxLength,
        ugcSource: ugcConfig.ugcSource,
        pdpBanner: _.get(ugcConfig, 'pdpBanner.fields.file.url')
    }];
}

/**
 * Determines what template to use for the PDP UGC component
 * @param {Object} ugcConfig Store UGC config
 * @returns {template} Correct template for the component
 */
export function determineTemplate(ugcConfig) {
    const displayType = _.get(ugcConfig, 'displayType', 'GALLERY');

    if (displayType === 'ROW') {
        return rowTemplate;
    } else if (displayType === 'CAROUSEL') {
        return carouselTemplate;
    }

    return galleryTemplate;
}

/**
 * Determines what template to use for the PDP UGC component
 * @param {Object} ugcConfig Store UGC config
 * @returns {template} Correct template for the component
 */
export function determineCarouselTemplate(ugcConfig) {
    const displayType = _.get(ugcConfig, 'displayType', 'GALLERY');

    if (displayType === 'CAROUSEL') {
        return carouselTrayTemplate;
    }

    return galleryTrayTemplate;
}

/**
 * Set the initial state in core.ugc
 *
 * State will be an array of X elements (depending on how many
 *  ugc components are on the page).
 *
 * Each element will consist of:
 *
 *  {
 *      reelId: String
 *      pageSize: Int
 *      filters : [ { displayText: ..., filterValue: ... } ]
 *      [filter]: String
 *      [productId]: String,
 *      captionMaxLength: Int
 *      [featuredFilter]: String
 *      [featuredPageSize]: Int
 *  }
 *
 * where filter is the active filter value
 * where productId is the active product id (if coming from the PDP)
 *
 * This options are passed into the getCuralatePostsSagas as query parameters
 *  to the UGC service
 *
 * Future Note: If deep linking of a filter is supported in the future,
 *  read the query parameter here and set
 *
 * @param {object} store The redux store
 * @returns {undefined} Returns nothing
 */
export function afterConstruction(store) {
    this.attachLoader(store);
    const initialState = [];

    for (let i = 0; i < this.ugcConfigs.length; i++) {
        initialState.push({
            config: _.cloneDeep(this.ugcConfigs[i])
        });
    }

    store.dispatch(saveUgcs(initialState));
    store.runSaga(getUGCPostsSaga, this.ugcConfigs).done.then(() => this.removeLoader());
}

/**
 * Attach component events to DOM
 * @param {object} store The redux store
 * @param {number} componentIndex The zero-based iteration of the current index
 * of UGC Components on the page
 * @returns {undefined}
 */
function attachEvents(store, componentIndex) {
    const isTouchDevice = _.get(this.getDeviceCharacteristics(), 'is_touch', false);
    const scope = `div[data-index="${componentIndex}"]`;
    const ugcTileSelector = `${scope} .js-ugc-tile`;
    const ugcCloseTraySelector = `${scope} .js-ugc-tray__close`;
    const ugcSeeMoreSelector = `${scope} .js-ugc-see-more`;
    const ugcFilterSelector = `${scope} .js-ugc-filter`;
    const ugcHoverClass = 'c-ugc-tile--hover';

    if (!isTouchDevice) {
        // When mouse moves into product tile, add hover class
        $(document).on(this.scopedEvent('mouseenter'), ugcTileSelector, (e) => {
            $(e.currentTarget).addClass(ugcHoverClass);
        });

        // When mouse leaves product tile, remove the hover class
        $(document).on(this.scopedEvent('mouseleave'), ugcTileSelector, () => {
            $(ugcTileSelector).removeClass(ugcHoverClass);
        });
    }

    $(document).on(this.scopedEvent('click'), ugcTileSelector, (e) => {
        const state = store.getState();

        // GA tracking on PDP
        const productStyleNumber = _.get(state, 'product.styleNumber', '');
        if (productStyleNumber) {
            fireGATrackingEvent(store, 'ugc image', productStyleNumber);
        }

        const numTiles = _.get(state, `core.ugc.${componentIndex}.items`, []).length;

        // grab the clicked tile index, and calculate where tray should go
        const numTilesInRow = determineNumTilesInRow(this.getDeviceCharacteristics());
        const clickedTileIndex = parseInt($(e.currentTarget).attr('data-tile-index'), 10);
        const tileToDisplayTray = getNextEligibleTileForTray(numTilesInRow, clickedTileIndex, numTiles);

        // Determine (from ugcSource) if we're throwing styleNumber or productID
        const ugcSource = _.get(state, `core.ugc.${componentIndex}.config.ugcSource`, '');
        const productIdentifier = (ugcSource === 'ENGAGE') ? 'styleNumbers' : 'productIds';

        // add the indexes to the store so they are available to the .j2
        store.dispatch(saveUgcSelectedIndex(componentIndex, tileToDisplayTray, clickedTileIndex));

        // Get products linked to the clicked tile
        const productIds = buildProductIdArray(store, componentIndex, clickedTileIndex);

        // Get data of each product linked, shove it in store
        store.runSaga(getTrayProductsSaga, componentIndex, productIds, productIdentifier);

        // Scroll to the clicked tile
        scrollToElement(`[data-index=${componentIndex}] [data-tile-index=${clickedTileIndex}]`, 0, 'medium');
    });

    $(document).on(this.scopedEvent('click touchend'), ugcCloseTraySelector, (e) => {
        // this prevents tap-delay bug UW-8712
        e.preventDefault();

        store.dispatch(clearUgcSelectedIndex(componentIndex));
    });

    $(document).on(this.scopedEvent('click touch'), ugcSeeMoreSelector, (e) => {
        const ugcConfig = this.ugcConfigs[componentIndex];
        const fetchCall = call(getMorePosts, componentIndex, ugcConfig);

        const updateOptions = {
            pendingText: ANIMATED_DOTS,
            pendingClass: 'is-pending',
            successClass: 'is-confirmed'
        };

        const delay = 1.0;

        store.dispatch(clearUgcSelectedIndex(componentIndex));

        store.runSaga(updateButtonTextSaga, fetchCall, e.target, delay, updateOptions);
    });

    $(document).on(this.scopedEvent('click touch'), ugcFilterSelector, (e) => {
        e.preventDefault();

        const filterIndex = parseInt($(e.target).parent()
            .attr('data-filter-index'), 10);
        const filterValue = _.get(
            store.getState(),
            `core.ugc.${componentIndex}.config.filters.${filterIndex}.filterValue`
        );

        if (filterIndex < 0) {
            // Clear, View All
            this.ugcConfigs[componentIndex].filterValue = null;
        } else {
            // Set the filter value
            this.ugcConfigs[componentIndex].filterValue = filterValue;
        }

        // Reset the pagination index back to the beginning of the stream
        this.ugcConfigs[componentIndex].offset = 0;

        // Close the tray
        store.dispatch(clearUgcSelectedIndex(componentIndex));

        // And repull all the posts
        const ugcConfig = this.ugcConfigs[componentIndex];

        // Resetting bookmark
        ugcConfig.bookmark = '';

        const fetchCall = call(getAndSaveInitialPostsSaga, componentIndex, ugcConfig);

        const updateOptions = {
            pendingText: ANIMATED_DOTS_SMALL_DARK,
            pendingClass: 'is-pending',
            successClass: 'is-active'
        };

        const delay = 1.0;

        store.dispatch(saveUgcSelectedFilter(componentIndex, filterValue));
        store.runSaga(updateButtonTextSaga, fetchCall, e.target, delay, updateOptions);
    });

    $(document).on(
        this.scopedEvent('click touchend'),
        `${ugcTileSelector} .js-heart-toggle`,
        { selector: ugcTileSelector },
        (e) => heartClicked(store, e)
    );
}

/**
 * Checks whether UGC component is turned off via Contentful config
 * @param  {Object}  store The redux store
 * @return {Boolean}        False if config is false, otherwise returns true
 */
export function isUgcActive(store) {
    const state = store.getState();

    return _.get(state, 'core.featureConfigs.browseDisplayOptions.ugcConfig.fields.isActive', false);
}

/**
 * Determines whether to initialize the PDP UGC component based on configs and DOM
 * @param  {[type]} store The redux store
 * @return {Boolean} True if PDP UGC component should initialize, else false
 */
export function shouldDisplayPdpUgc(store) {
    if (!isUgcActive(store)) {
        return false;
    }

    return $(this.templateLocation).length > 0;
}

/**
 * Determines whether to initialize the default category-level UGC component based on configs and DOM
 * @return {Boolean} True if the template location exists
 */
export function shouldDisplayUgc() {
    return $(this.templateLocation).length > 0;
}

/**
 * Initializing component should pass in an override for 'feature'
 *
 * PDP should override `getUgcConfigs` to pull the ugc configs.
 * PDP should override `feature` to `product`
 * Homepage and Help should override `feature`
 *
 * @param {object} store The redux store
 * @param {object} overrides Component-level overrides config
 * @returns {object} Returns the component utils generator function
 */
export function init(store, overrides = {}) {
    const feature = overrides.feature || 'category';
    const loader = overrides.loader || {};
    const getUgcConfigs = overrides.getUgcConfigs || getUgcConfigsFromContentful;

    overrides.ugcConfigs = getUgcConfigs(store, feature);

    const config = {
        children: [
            [ugcCarouselComponentInit, {
                ugcConfigs: overrides.ugcConfigs,
                template: determineCarouselTemplate(overrides.ugcConfigs[0]),
                multiwatch: overrides.carouselMultiWatch
            }]
        ],
        attachEvents: attachEvents,
        templateLocation: '.dom-ugc',
        watch: ['core.ugc', 'core.loyaltyForm.signupSuccess'],
        // multiwatch: (index) => `core.ugc.${index}.items`,
        name: 'ugcComponent',
        renderType: mDom,
        afterConstruction: afterConstruction,
        afterRender: afterRender,
        onStateChange: onStateChange,
        generateDictionary: generateDictionary,
        template: determineTemplate(overrides.ugcConfigs[0]),
        loader: loader,
        feature: feature,
        isComponentValid: shouldDisplayUgc,
        multiple: () => overrides.ugcConfigs.length,
        lazyLoad: false
    };

    return generate(config, store, overrides, getDefaultOptions(store));
}
